import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, withQuery, money, date } from '../api.js';
import Lookup from '../components/Lookup.jsx';

/**
 * Плащания по банково извлечение.
 *
 * Качва се справката от банката, програмата предлага кои движения по кои
 * фактури са, човекът потвърждава. До потвърждението нищо не се записва.
 *
 * Редовете идват разпределени по състояние и всяко състояние иска
 * различно внимание, затова стоят в отделни купчини. Отгоре са тези, по
 * които има работа; отдолу — тези, които програмата вече е обяснила и
 * които рядко се отварят.
 */

const GROUPS = [
  {
    key: 'matched',
    title: 'Разпознати плащания',
    hint: 'Програмата намери по кои фактури са. Прегледай и потвърди.',
    open: true,
  },
  {
    key: 'guess',
    title: 'Предположени — по най-старите неплатени',
    hint: 'Основанието не сочи фактура и сумата не се връзва с нищо. Тук парите '
      + 'са разнесени по най-старите неплатени, защото такъв е обичаят — но това '
      + 'е догадка, не разпознаване. Провери всеки ред с „напасни", преди да го '
      + 'отметнеш. Често такъв превод е по фактура отпреди периода и не му е '
      + 'мястото никъде.',
    open: false,
  },
  {
    key: 'unclear',
    title: 'Неясни',
    hint: 'Фирмата е позната и има неплатени фактури, но сумата не се връзва '
      + 'с тях. Обикновено е плащане по фактура отпреди периода.',
    open: false,
  },
  {
    key: 'unknown-partner',
    title: 'Непозната фирма',
    hint: 'Не намерих такъв контрагент — заплати, данъци, телефон, наеми. '
      + 'Ако някой от тях е наш, посочи го и ще го запомня.',
    open: false,
  },
  {
    key: 'ignored',
    title: 'Подминавани имена',
    hint: 'Отбелязани веднъж като „не е контрагент" — заплати, данъци, наеми, '
      + 'собствени сметки. Ако някое е сбъркано, посочи фирмата и се връща.',
    open: false,
  },
  {
    key: 'already',
    title: 'Вече отразени',
    hint: 'Основанието сочи фактура, която при нас е платена.',
    open: false,
  },
  {
    key: 'nothing-open',
    title: 'Няма какво да се плаща',
    hint: 'Фирмата е позната, но няма неплатени фактури при нея.',
    open: false,
  },
  {
    key: 'old-period',
    title: 'От времето на старата програма',
    hint: 'Преди преминаването сметките ги е водила старата програма и резултатът '
      + 'ѝ е пренесен: всеки стар документ е затворен, а неплатеното стои като '
      + 'салдо. Тези движения вече са отчетени и не се предлагат — отразяването '
      + 'им втори път би удвоило плащанията.',
    open: false,
  },
  {
    key: 'imported',
    title: 'Отразени от предишно качване',
    hint: 'Референцията на банката вече стои по записано плащане.',
    open: false,
  },
];

const countStates = (rows) => rows.reduce((acc, r) => {
  acc[r.state] = (acc[r.state] ?? 0) + 1;
  return acc;
}, {});

export default function BankImport() {
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [openGroups, setOpenGroups] = useState(
    () => new Set(GROUPS.filter((g) => g.open).map((g) => g.key))
  );
  const [applied, setApplied] = useState(null);

  async function upload(file) {
    if (!file) return;
    setBusy(true);
    setError(null);
    setApplied(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/bank-import/parse', {
        method: 'POST', credentials: 'same-origin', body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error ?? `Грешка ${res.status}`);
      setResult(data);
      // Разпознатите с точна сума се предлагат отметнати — те са
      // очакваното, а не изключението.
      setPicked(new Set(data.rows
        .filter((r) => r.state === 'matched' && r.exact)
        .map((r) => r.reference)));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const byGroup = useMemo(() => {
    const out = new Map(GROUPS.map((g) => [g.key, []]));
    for (const r of result?.rows ?? []) {
      const k = out.has(r.state) ? r.state : 'unclear';
      out.get(k).push(r);
    }
    return out;
  }, [result]);

  const chosen = (result?.rows ?? []).filter((r) => picked.has(r.reference));
  const chosenSum = Math.round(chosen.reduce((s, r) => s + r.amount, 0) * 100) / 100;

  function toggle(ref) {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(ref)) next.delete(ref); else next.add(ref);
      return next;
    });
  }

  /**
   * Посочената фирма се запомня веднага, отделно от плащането.
   *
   * Повечето непознати имена нямат какво да платят — заплати, данъци,
   * собствени сметки — и никога не стигат до „Отрази избраните". Ако
   * запомнянето чакаше него, същите имена щяха да излизат непознати при
   * всяко качване. Затова посочването е самостоятелно.
   *
   * Движението се преценява наново с вече известната фирма и се мести в
   * своята купчина, без файлът да се качва пак.
   */
  async function link(correspondent, partnerId, partnerName, ignore = false) {
    if (!partnerId && !ignore) return;
    setError(null);
    const mine = (result?.rows ?? []).filter((r) => r.correspondent === correspondent);
    try {
      const res = await api.post('/api/bank-correspondents', {
        correspondent,
        partnerId: partnerId ?? null,
        ignore,
        rows: mine.map((r) => ({
          date: r.date, direction: r.direction, amount: r.amount,
          basis: r.basis, reference: r.reference, kind: r.kind,
        })),
      });
      const fresh = new Map((res.rows ?? []).map((r) => [r.reference, r]));
      setResult((old) => {
        const rows = old.rows.map((r) => (r.correspondent === correspondent
          ? fresh.get(r.reference) ?? {
            ...r,
            state: ignore ? 'ignored' : r.state,
            partnerId: ignore ? null : partnerId,
            partnerName: ignore ? null : partnerName,
            matchedBy: ignore ? 'подминава се' : 'запомнено',
          }
          : r));
        return { ...old, rows, counts: countStates(rows) };
      });
      // Каквото стана предложение с точна сума, се отмята само.
      setPicked((s) => {
        const next = new Set(s);
        for (const r of fresh.values()) {
          if (r.state === 'matched' && r.exact) next.add(r.reference);
        }
        return next;
      });
    } catch (err) {
      setError(err.message);
    }
  }

  /**
   * Ръчно избраните фактури влизат по реда и той става предложение.
   *
   * Съвпадне ли сборът със сумата на превода, редът се отмята сам —
   * човекът вече е свършил преценката и второ щракване е излишно.
   */
  function setRowDocuments(row, documents, exact) {
    const sum = Math.round(documents.reduce((s, d) => s + d.remaining, 0) * 100) / 100;
    setResult((old) => {
      const rows = old.rows.map((r) => (r.reference === row.reference
        ? { ...r, state: 'matched', by: 'на ръка', documents, documentsSum: sum, exact }
        : r));
      return { ...old, rows, counts: countStates(rows) };
    });
    setPicked((s) => new Set(s).add(row.reference));
  }

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      const rows = chosen.map((r) => {
        const partnerId = r.partnerId;
        // Разпределението пълни фактурите отгоре надолу; каквото не
        // стигне, остава като аванс по сметката на фирмата.
        let left = Math.round(r.amount * 100);
        const allocations = [];
        for (const d of r.documents ?? []) {
          if (left <= 0) break;
          const own = Math.round(d.remainingOwn * 100);
          const take = Math.min(left, own);
          allocations.push({ documentId: d.id, amount: take / 100 });
          left -= Math.min(left, Math.round(d.remaining * 100));
        }
        return {
          reference: r.reference,
          date: r.date,
          direction: r.direction,
          amount: r.amount,
          partnerId,
          correspondent: r.correspondent,
          allocations,
        };
      });

      const res = await api.post('/api/bank-import/apply', { rows });
      setApplied(res);
      setPicked(new Set());
      // Отразеното изчезва от предложенията при следващо качване; тук
      // редовете се отбелязват веднага, за да не се цъкат втори път.
      setResult((old) => ({
        ...old,
        rows: old.rows.map((r) => (res.done.some((d) => d.reference === r.reference)
          ? { ...r, state: 'imported' } : r)),
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Банково извлечение</h1>
        <div className="spacer" />
        <Link to="/debtors">← към длъжниците</Link>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <div className="card-body">
          <p className="hint" style={{ marginTop: 0 }}>
            Качи справката за движенията по сметката, както я дава банката. Взимат се
            само преводите от клиенти и към доставчици — картови транзакции, такси,
            лихви и комисиони се подминават. Нищо не се записва, докато не потвърдиш.
          </p>
          <input
            ref={fileInput}
            type="file"
            accept=".xls,.xlsx,.html,.htm,.csv,.txt"
            onChange={(e) => upload(e.target.files?.[0])}
            disabled={busy}
          />
          {busy && <span className="muted" style={{ marginLeft: 10 }}>Чете се…</span>}
        </div>
      </div>

      {applied && (
        <div className="note">
          Отразени {applied.done.length} плащания
          {applied.skipped.length > 0 && `, пропуснати ${applied.skipped.length}`}.
          {applied.skipped.length > 0 && (
            <ul style={{ margin: '6px 0 0 18px' }}>
              {applied.skipped.slice(0, 8).map((s) => (
                <li key={s.reference} className="muted">{s.reference}: {s.reason}</li>
              ))}
            </ul>
          )}
          <div style={{ marginTop: 6 }}>
            <button className="link" onClick={() => navigate('/debtors')}>
              виж какво остава по длъжниците
            </button>
          </div>
        </div>
      )}

      {result && (
        <>
          <div className="tiles" style={{ marginTop: 12 }}>
            <div className="tile">
              <div className="label">Движения във файла</div>
              <div className="value">{result.total}</div>
              <div className="sub">{result.fileName}</div>
            </div>
            <div className="tile">
              <div className="label">Разпознати</div>
              <div className="value">{result.counts.matched ?? 0}</div>
              <div className="sub">предложени за отразяване</div>
            </div>
            <div className="tile">
              <div className="label">Избрани</div>
              <div className="value">{money(chosenSum)}</div>
              <div className="sub">{chosen.length} движения</div>
            </div>
          </div>

          <div className="toolbar" style={{ marginTop: 12 }}>
            <button className="primary" onClick={apply} disabled={busy || chosen.length === 0}>
              {busy ? 'Записва…' : `Отрази избраните (${chosen.length})`}
            </button>
            <button onClick={() => setPicked(new Set())} disabled={chosen.length === 0}>
              Изчисти избора
            </button>
          </div>

          {GROUPS.map((g) => {
            const list = byGroup.get(g.key) ?? [];
            if (!list.length) return null;
            const isOpen = openGroups.has(g.key);
            return (
              <div className="card" key={g.key} style={{ marginTop: 12 }}>
                <div className="card-head" style={{ cursor: 'pointer' }}
                  onClick={() => setOpenGroups((s) => {
                    const next = new Set(s);
                    if (next.has(g.key)) next.delete(g.key); else next.add(g.key);
                    return next;
                  })}>
                  {isOpen ? '▾' : '▸'} {g.title}
                  <div style={{ flex: 1 }} />
                  <span className="badge">{list.length}</span>
                </div>
                {isOpen && (g.key === 'unknown-partner' || g.key === 'ignored') && (
                  <>
                    <div className="card-body" style={{ paddingBottom: 4 }}>
                      <p className="hint" style={{ margin: 0 }}>{g.hint}</p>
                    </div>
                    <UnknownNames rows={list} onLink={link} ignored={g.key === 'ignored'} />
                  </>
                )}
                {isOpen && g.key !== 'unknown-partner' && g.key !== 'ignored' && (
                  <>
                    <div className="card-body" style={{ paddingBottom: 4 }}>
                      <p className="hint" style={{ margin: 0 }}>{g.hint}</p>
                    </div>
                    <div className="table-wrap">
                      <table className="compact">
                        <thead>
                          <tr>
                            <th style={{ width: 24 }} />
                            <th style={{ width: 90 }}>Дата</th>
                            <th className="num" style={{ width: 100 }}>Сума</th>
                            <th>Кореспондент</th>
                            <th>Фирма при нас</th>
                            <th>Основание</th>
                            <th>Фактури</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.map((r) => (
                            <Row
                              key={r.reference || `${r.date}-${r.amount}-${r.correspondent}`}
                              row={r}
                              picked={picked.has(r.reference)}
                              onToggle={() => toggle(r.reference)}
                              onLink={(id, name) => link(r.correspondent, id, name)}
                              onMatch={(docs, exact) => setRowDocuments(r, docs, exact)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}

/**
 * Непознатите имена — по едно на фирма, не по едно на движение.
 *
 * Тук работата е с имената: 271 движения идват от стотина фирми, а
 * едно и също име се повтаря по 46 пъти (НАП). Показването им поред би
 * значело да се цъка едно и също отново и отново. Затова редът е
 * фирмата, а движенията ѝ са число до нея.
 */
function UnknownNames({ rows, onLink, ignored = false }) {
  const names = useMemo(() => {
    const by = new Map();
    for (const r of rows) {
      const k = r.correspondent;
      if (!by.has(k)) {
        by.set(k, { name: k, count: 0, inSum: 0, outSum: 0, first: r.date, last: r.date });
      }
      const n = by.get(k);
      n.count += 1;
      if (r.direction === 'in') n.inSum += r.amount; else n.outSum += r.amount;
      if (r.date && (!n.first || r.date < n.first)) n.first = r.date;
      if (r.date && (!n.last || r.date > n.last)) n.last = r.date;
    }
    // Най-често срещаните отгоре: тях си струва да свържеш първо.
    return [...by.values()].sort((a, b) => b.count - a.count
      || (b.inSum + b.outSum) - (a.inSum + a.outSum));
  }, [rows]);

  return (
    <div className="table-wrap">
      <table className="compact">
        <thead>
          {/* Двете имена стоят едно до друго: работата тук е да се
              свържат, а пет колони между тях късат погледа. */}
          <tr>
            <th style={{ width: '32%' }}>Име в извлечението</th>
            <th style={{ width: '32%' }}>Фирма при нас</th>
            <th className="num" style={{ width: 90 }}>Движения</th>
            <th className="num" style={{ width: 110 }}>От тях</th>
            <th className="num" style={{ width: 110 }}>Към тях</th>
            <th style={{ width: 150 }}>Период</th>
          </tr>
        </thead>
        <tbody>
          {names.map((n) => (
            <tr key={n.name}>
              <td><b>{n.name}</b></td>
              <td>
                <Lookup url="/api/partners" params={{ withEik: true }} value={null} label=""
                  placeholder={ignored ? 'върни като фирма' : 'посочи фирмата'}
                  onPick={(id, name) => onLink(n.name, id, name)} />
                {/* Заплати, данъци, наеми и собствени сметки нямат фирма
                    зад себе си. Отбелязват се веднъж и не се връщат. */}
                {!ignored && (
                  <button className="link" style={{ marginLeft: 8, fontSize: 11 }}
                    onClick={() => onLink(n.name, null, null, true)}>
                    не е контрагент
                  </button>
                )}
              </td>
              <td className="num muted">{n.count}</td>
              <td className="num">{n.inSum > 0 ? money(n.inSum) : <span className="muted">—</span>}</td>
              <td className="num">{n.outSum > 0 ? money(n.outSum) : <span className="muted">—</span>}</td>
              <td className="muted" style={{ fontSize: 11 }}>
                {n.first === n.last ? date(n.first) : `${date(n.first)} – ${date(n.last)}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Ръчно напасване на движение към фактури.
 *
 * Отваря се на самия ред, под него — човекът гледа сумата от банката и
 * неплатените фактури на тази фирма едновременно и сам решава кои
 * покрива преводът. Сборът на отбелязаното стои до сумата на превода,
 * за да се сравняват без сметки наум.
 *
 * Трябва, защото програмата не гадае, когато сборът не излиза: банката
 * плаща 498.28, а намерените фактури правят 233.86 — останалото е по
 * фактури отпреди периода или по такива, които не сме въвели.
 */
function MatchPanel({ row, onCancel, onDone }) {
  const [docs, setDocs] = useState(null);
  const [chosen, setChosen] = useState(() => new Set((row.documents ?? []).map((d) => d.id)));
  const [error, setError] = useState(null);

  useEffect(() => {
    const side = row.direction === 'in' ? 'receivable' : 'payable';
    api.get(withQuery('/api/reports/debts', { side, partnerId: row.partnerId }))
      .then((res) => setDocs((res.rows ?? [])
        .filter((d) => d.document_id)
        .map((d) => ({
          id: Number(d.document_id),
          invoiceNo: d.invoice_no,
          docNumber: d.doc_number,
          docDate: d.doc_date,
          currency: d.currency,
          remaining: Number(d.remaining),
          remainingOwn: Math.round((Number(d.total) - Number(d.paid_amount)) * 100) / 100,
        }))
        .sort((a, b) => String(a.docDate).localeCompare(String(b.docDate)))))
      .catch((e) => setError(e.message));
  }, [row.partnerId, row.direction]);

  if (error) return <div className="error">{error}</div>;
  if (!docs) return <div className="empty">Зареждане…</div>;

  const picked = docs.filter((d) => chosen.has(d.id));
  const sum = Math.round(picked.reduce((s, d) => s + d.remaining, 0) * 100) / 100;
  const diff = Math.round((row.amount - sum) * 100) / 100;

  return (
    <div className="settle">
      <div className="settle-check">
        <span>по банка: <b>{money(row.amount)}</b></span>
        <span>избрани фактури: <b>{money(sum)}</b></span>
        {Math.abs(diff) < 0.005
          ? <span className="badge ok">сумите съвпадат</span>
          : diff > 0
            ? <span className="badge warn">
                остават {money(diff)} — ще станат аванс по сметката
              </span>
            : <span className="badge warn">
                избрано е с {money(-diff)} повече — последната ще остане частично платена
              </span>}
      </div>

      {docs.length === 0 ? (
        <p className="empty">Няма неплатени фактури при тази фирма.</p>
      ) : (
        <div className="table-wrap" style={{ maxHeight: 320 }}>
          <table className="inner">
            <thead>
              <tr>
                <th style={{ width: 24 }} />
                <th>Фактура</th>
                <th>Документ</th>
                <th>Дата</th>
                <th className="num">Остава</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className={chosen.has(d.id) ? 'picked-row' : undefined}>
                  <td>
                    <input type="checkbox" checked={chosen.has(d.id)}
                      onChange={() => setChosen((s) => {
                        const next = new Set(s);
                        if (next.has(d.id)) next.delete(d.id); else next.add(d.id);
                        return next;
                      })} />
                  </td>
                  <td>{d.invoiceNo || <span className="muted">—</span>}</td>
                  <td className="muted">{d.docNumber}</td>
                  <td className="muted">{date(d.docDate)}</td>
                  <td className="num">{money(d.remaining)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="toolbar" style={{ marginTop: 8, marginBottom: 0 }}>
        <button onClick={onCancel}>Откажи</button>
        <button className="primary" disabled={picked.length === 0}
          onClick={() => onDone(picked, Math.abs(diff) < 0.005)}>
          Готово ({picked.length})
        </button>
      </div>
    </div>
  );
}

function Row({ row: r, picked, onToggle, onLink, onMatch }) {
  const partnerId = r.partnerId;
  const canPick = !!partnerId && r.state !== 'imported';
  // Сгрешеното се поправя на място — и налучканото от програмата, и
  // посоченото на ръка. Поправката се помни като всяко друго посочване.
  const [changing, setChanging] = useState(false);
  const [matching, setMatching] = useState(false);
  // Ръчното напасване има смисъл само докато има какво да се плаща.
  const canMatch = !!partnerId && r.state !== 'imported' && r.state !== 'already';

  const main = (
    <tr className={picked ? 'picked-row' : undefined}>
      <td>
        {canPick && (
          <input type="checkbox" checked={picked} onChange={onToggle} />
        )}
      </td>
      <td>{r.date ? date(r.date) : '—'}</td>
      <td className="num">
        <b>{money(r.amount)}</b>
        <div className="muted" style={{ fontSize: 10 }}>
          {r.direction === 'in' ? 'от клиент' : 'към доставчик'}
        </div>
      </td>
      <td>{r.correspondent}</td>
      <td>
        {partnerId && !changing ? (
          <>
            <Link to={`/partners/${partnerId}`}>{r.partnerName}</Link>
            {r.matchedBy && (
              <span className="muted" style={{ fontSize: 10 }}> · {r.matchedBy}</span>
            )}
            <button className="link" style={{ marginLeft: 8, fontSize: 11 }}
              onClick={() => setChanging(true)}>смени</button>
          </>
        ) : (
          <>
            <Lookup url="/api/partners" params={{ withEik: true }} value={null} label=""
              placeholder="посочи фирмата"
              onPick={(id, name) => { setChanging(false); onLink(id, name); }} />
            {changing && (
              <button className="link" style={{ marginLeft: 8, fontSize: 11 }}
                onClick={() => setChanging(false)}>откажи</button>
            )}
          </>
        )}
      </td>
      <td className="muted" style={{ maxWidth: 240 }}>
        {r.basis}
        {/* Банката реже основанието и продължава списъка в съседната
            колона — показва се, за да си личи откъде идват номерата. */}
        {r.note && <span style={{ opacity: 0.75 }}> {r.note}</span>}
      </td>
      <td>
        {r.documents?.length ? (
          <>
            {r.documents.map((d) => d.invoiceNo || d.docNumber).join(', ')}
            {!r.exact && (
              <div className="badge warn" style={{ marginTop: 2 }}>
                фактурите правят {money(r.documentsSum)}
              </div>
            )}
          </>
        ) : r.state === 'already' ? (
          <span className="muted">
            {r.settledPayment
              ? `записано с плащане ${r.settledPayment}`
              : `платена: ${(r.settledNumbers ?? []).join(', ')}`}
          </span>
        ) : <span className="muted">—</span>}
        {canMatch && (
          <button className="link" style={{ marginLeft: 8, fontSize: 11 }}
            onClick={() => setMatching((v) => !v)}>
            {matching ? 'затвори' : 'напасни'}
          </button>
        )}
      </td>
    </tr>
  );

  // Панелът за напасване стои под самия ред — сумата от банката и
  // неплатените фактури се гледат заедно.
  return matching ? (
    <>
      {main}
      <tr className="detail">
        <td />
        <td colSpan={6}>
          <MatchPanel
            row={r}
            onCancel={() => setMatching(false)}
            onDone={(docs, exact) => { setMatching(false); onMatch(docs, exact); }}
          />
        </td>
      </tr>
    </>
  ) : main;
}
